import React, { useEffect, useRef } from 'react';
import { UseFormRegister, FieldError, RegisterOptions } from 'react-hook-form';
import MarkdownEditor from '../Common/MarkdownEditor';

interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'value' | 'onChange'> {
  label: string;
  name: string;
  register: UseFormRegister<any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  rules?: RegisterOptions; // Ajout de la prop rules
  error?: FieldError;
  markdown?: boolean; // Enable/disable markdown mode (default: true)
  preview?: 'edit' | 'live' | 'preview'; // Preview mode for markdown editor
  height?: number; // Height of the editor
}

const Textarea: React.FC<TextareaProps> = ({ 
  label, 
  name, 
  register, 
  rules, 
  error, 
  markdown = true,
  preview = 'live',
  height = 200,
  ...rest 
}) => {
  const textareaId = `textarea-${name}`;
  const registerReturn = register(name, rules);
  const [value, setValue] = React.useState<string>('');
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const isInitialized = useRef(false);

  // Initialize value from defaultValues if available
  useEffect(() => {
    if (!isInitialized.current && hiddenInputRef.current) {
      // Get initial value from the hidden input's default value or from the form
      const form = hiddenInputRef.current.closest('form');
      if (form) {
        const formData = new FormData(form);
        const initialValue = formData.get(name);
        if (initialValue && typeof initialValue === 'string') {
          setValue(initialValue);
          isInitialized.current = true;
        }
      }
    }
  }, [name]);

  const handleChange = (val: string | undefined) => {
    const newValue = val || '';
    setValue(newValue);
    
    // Update hidden input and trigger react-hook-form
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = newValue;
      // Create and dispatch input event
      const inputEvent = new Event('input', { bubbles: true });
      hiddenInputRef.current.dispatchEvent(inputEvent);
      
      // Also call register's onChange
      if (registerReturn.onChange) {
        registerReturn.onChange({
          target: hiddenInputRef.current,
          type: 'change',
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handleBlur = () => {
    if (hiddenInputRef.current && registerReturn.onBlur) {
      registerReturn.onBlur({
        target: hiddenInputRef.current,
        type: 'blur',
      } as React.FocusEvent<HTMLInputElement>);
    }
  };

  return (
    <div className="mb-4">
      <label htmlFor={textareaId} className="block text-sm font-medium text-foreground mb-1">
        {label}
      </label>
      {markdown ? (
        <>
          {/* Hidden input for react-hook-form */}
          <input
            type="hidden"
            {...registerReturn}
            ref={(e) => {
              hiddenInputRef.current = e;
              if (registerReturn.ref) {
                if (typeof registerReturn.ref === 'function') {
                  registerReturn.ref(e);
                } else {
                  registerReturn.ref.current = e;
                }
              }
            }}
            value={value}
          />
          <MarkdownEditor
            value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            error={!!error}
            preview={preview}
            height={height}
            placeholder={rest.placeholder as string}
            disabled={rest.disabled}
            className="mt-1"
          />
        </>
      ) : (
        <textarea
          id={textareaId}
          {...registerReturn}
          {...rest}
          className={`mt-1 block w-full px-3 py-2 rounded-md border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background sm:text-sm ${error ? 'border-destructive' : 'border-input'}`}
          aria-invalid={error ? 'true' : 'false'}
        />
      )}
      {error && <p className="mt-1 text-xs text-destructive">{error.message}</p>}
    </div>
  );
};

export default Textarea;
